import { z } from 'zod';
import isNil from 'lodash/isNil';

import {
  IS_REQUIRED,
  isGreaterThan,
  isGreaterThanOrEqualTo,
  isLessThan,
  isLessThanOrEqualTo,
} from '@occc-app/lib/constants/validationMessages';

import { GeneralSurveyFragment, LocationFragment, LocationGroupFragment } from '@occc/graphql';
import { AudienceSurveyResponseSliderOperator, SurveysValuesItem } from './types';
import {
  getIsSingleOrMultipleChoiceQuestion,
  getIsSliderQuestionResponded,
  getIsSliderQuestionRespondedAndBetweenOperator,
  getSurveyQuestion,
} from './utils';

const valueValidatorByOperatorZod: Record<
  Exclude<AudienceSurveyResponseSliderOperator, 'is-between'>,
  (value: number, min: number, max: number) => string | void
> = {
  'is-equal-to': (value, min, max) =>
    value < min || value > max ? `Must be between ${min} and ${max}` : undefined,

  'is-greater-than': (value, _min, max) => (value >= max ? isLessThan(max) : undefined),

  'is-greater-than-or-equal': (value, _min, max) =>
    value > max ? isLessThanOrEqualTo(max) : undefined,

  'is-less-than': (value, min, _max) => (value <= min ? isGreaterThan(min) : undefined),

  'is-less-than-or-equal': (value, min, _max) =>
    value < min ? isGreaterThanOrEqualTo(min) : undefined,
};

type OptionalArg<T> = undefined extends T ? [data?: T] : [data: T];

function reportValidationIssues<S extends z.ZodType<any>>(
  schema: S,
  ctx: z.RefinementCtx,
  ...[data]: OptionalArg<z.infer<S>>
) {
  const result = schema.safeParse(data);
  result.error?.issues.forEach(ctx.addIssue);
}

const zodSurveyAnswersSchema = z.custom<SurveysValuesItem>(
  (data: SurveysValuesItem) => {
    if (!data.survey) return true;
    const surveyQuestion = getSurveyQuestion(data.survey as GeneralSurveyFragment);
    const isInvalidResponse =
      getIsSingleOrMultipleChoiceQuestion(surveyQuestion) &&
      data.respondedStatus === 'responded' &&
      data.surveyAnswers?.length === 0;

    return !isInvalidResponse;
  },
  { message: IS_REQUIRED, path: ['surveyAnswers'] },
);

const getValueFieldSchema = (data: SurveysValuesItem, min: number, max: number) => {
  let message: string | undefined = undefined;

  if (getIsSliderQuestionResponded(data) && data.sliderOperator !== 'is-between') {
    if (isNil(data.value)) {
      message = IS_REQUIRED;
    } else {
      const errorMessage = valueValidatorByOperatorZod[data.sliderOperator](data.value, min, max);
      if (errorMessage) {
        message = errorMessage;
      }
    }
  }
  return z.custom<undefined>(() => !message, {
    path: ['value'],
    message,
  });
};

const getMinValueSchema = (data: {
  surveyMin: number;
  surveyMax: number;
  minValue?: number;
  maxValue?: number;
}) => {
  const { minValue, maxValue, surveyMin, surveyMax } = data;
  let message: string | undefined = undefined;

  if (isNil(minValue)) {
    message = IS_REQUIRED;
  } else if (!isNil(maxValue) && maxValue <= minValue) {
    message = isGreaterThan(minValue);
  } else if (minValue < surveyMin) {
    message = isGreaterThanOrEqualTo(surveyMin);
  } else if (minValue >= surveyMax) {
    message = isLessThan(surveyMax);
  }
  return z.custom<undefined>(() => !message, {
    path: ['minValue'],
    message,
  });
};

const getMaxValueSchema = (data: {
  surveyMin: number;
  surveyMax: number;
  minValue?: number;
  maxValue?: number;
}) => {
  const { minValue, maxValue, surveyMin, surveyMax } = data;
  let message: string | undefined;

  if (isNil(maxValue)) {
    message = IS_REQUIRED;
  } else if (!isNil(minValue) && maxValue <= minValue) {
    message = isGreaterThan(minValue);
  } else if (maxValue > surveyMax) {
    message = isLessThanOrEqualTo(surveyMax);
  } else if (maxValue <= surveyMin) {
    message = isGreaterThan(surveyMin);
  }

  return z.custom<undefined>(() => !message, {
    path: ['maxValue'],
    message,
  });
};

const zodSurveySchema = z
  .object({
    respondedStatus: z.enum(['responded', 'not-responded'], { message: IS_REQUIRED }),
    survey: z.object({}).passthrough().optional() as unknown as z.ZodType<
      GeneralSurveyFragment | undefined
    >,
    locations: z.array(z.custom<LocationFragment>()).optional(),
    locationGroups: z.array(z.custom<LocationGroupFragment>()).optional(),
    surveyAnswers: z.array(z.string()).optional(),
    sliderOperator: z.string({
      required_error: IS_REQUIRED,
    }) as z.ZodType<AudienceSurveyResponseSliderOperator>,
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    value: z.number().optional(),
  })
  .superRefine((data, ctx) => {
    reportValidationIssues(zodSurveyAnswersSchema, ctx, data);

    if (!data.survey) return;
    const surveyQuestion = getSurveyQuestion(data.survey);
    if (surveyQuestion.__typename !== 'SurveyQuestionIntSlider') return;

    const surveyMin = surveyQuestion.min || 0;
    const surveyMax = surveyQuestion.max || 2;
    const valueFieldSchema = getValueFieldSchema(data, surveyMin, surveyMax);
    reportValidationIssues(valueFieldSchema, ctx);

    if (
      getIsSliderQuestionRespondedAndBetweenOperator(data) &&
      data.sliderOperator === 'is-between'
    ) {
      const { minValue, maxValue } = data;
      const minValueSchema = getMinValueSchema({ minValue, maxValue, surveyMin, surveyMax });
      reportValidationIssues(minValueSchema, ctx);
      const maxValueSchema = getMaxValueSchema({ minValue, maxValue, surveyMin, surveyMax });
      reportValidationIssues(maxValueSchema, ctx);
    }
  });

export const surveysSchema = z
  .array(zodSurveySchema)
  .optional()
  .superRefine((surveys, ctx) => {
    surveys?.forEach((data, index) => {
      if (data.survey) return;
      if (
        index > 0 ||
        !!data.surveyAnswers?.length ||
        !!data.locations?.length ||
        !!data.locationGroups?.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: IS_REQUIRED,
          path: [index, 'survey'],
        });
      }
    });
  });
